import { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X, Camera, TextT, CircleNotch } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { FoodAnalysis } from './FoodFactsModal';

interface AddFoodModalProps {
  isOpen: boolean;
  onClose: () => void;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snacks';
  onCameraCapture?: (file: File) => void;
  onTextAnalysisComplete?: (analysis: FoodAnalysis) => void;
  lang?: string;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: ${theme.zIndex.modal};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;
`;

const Modal = styled.div`
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 375px;
  max-height: 85vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border-subtle);
  position: sticky;
  top: 0;
  background: var(--color-bg);
  z-index: 1;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  text-transform: capitalize;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-border-subtle);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  cursor: pointer;
  transition: background ${theme.transitions.fast};

  &:hover {
    background: var(--color-border);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  padding: 16px 24px;
  gap: 8px;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-border)')};
  background: ${({ $active }) => ($active ? 'rgba(131, 76, 201, 0.2)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-text)')};
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: var(--color-primary);
  }
`;

const Content = styled.div`
  padding: 0 24px 24px;
`;

const CameraSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 0;
`;

const CameraButton = styled.button`
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: rgba(131, 76, 201, 0.15);
  border: 2px dashed var(--color-primary);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--color-primary);
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: rgba(131, 76, 201, 0.25);
  }

  span {
    font-family: ${theme.typography.fontFamily.secondary};
    font-size: 12px;
  }
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 15px;
  outline: none;
  resize: none;
  min-height: 100px;
  transition: border-color ${theme.transitions.fast};

  &::placeholder {
    color: var(--color-border);
  }

  &:focus {
    border-color: var(--color-primary);
  }
`;

const AnalyzeButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  background: ${({ $disabled }) => ($disabled ? 'rgba(131, 76, 201, 0.3)' : 'var(--color-primary)')};
  color: var(--color-text);
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all ${theme.transitions.fast};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: var(--color-primary-dark);
  }
`;

const Spinner = styled(CircleNotch)`
  animation: ${spin} 1s linear infinite;
`;

const ErrorText = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: #FF6B6B;
  text-align: center;
  margin: 12px 0 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

export const AddFoodModal: React.FC<AddFoodModalProps> = ({
  isOpen,
  onClose,
  mealType,
  onCameraCapture,
  onTextAnalysisComplete,
  lang,
}) => {
  const [activeTab, setActiveTab] = useState<'camera' | 'text'>('text');
  const [foodDescription, setFoodDescription] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isMountedRef = useRef(true);

  // Track mount state so async fetch callbacks don't update state after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleClose = useCallback(() => {
    setFoodDescription('');
    setActiveTab('text');
    setAnalyzeError(null);
    onClose();
  }, [onClose]);

  // Close on escape key (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleAnalyze = async () => {
    if (!foodDescription.trim() || isAnalyzing) return;

    try {
      setIsAnalyzing(true);
      setAnalyzeError(null);

      const response = await fetch('/api/chat/analyze-food-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ text: foodDescription, lang: lang || 'en' }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Convert to FoodAnalysis format
          const analysis = data.data;
          const totalMacroGrams = analysis.carbsGrams + analysis.proteinGrams + analysis.fatGrams;

          const foodAnalysis: FoodAnalysis = {
            name: analysis.foodName,
            servingSize: analysis.servingSize,
            calories: analysis.calories,
            macros: {
              carbs: {
                grams: analysis.carbsGrams,
                percentage: totalMacroGrams > 0 ? Math.round((analysis.carbsGrams / totalMacroGrams) * 100) : 0,
              },
              protein: {
                grams: analysis.proteinGrams,
                percentage: totalMacroGrams > 0 ? Math.round((analysis.proteinGrams / totalMacroGrams) * 100) : 0,
              },
              fat: {
                grams: analysis.fatGrams,
                percentage: totalMacroGrams > 0 ? Math.round((analysis.fatGrams / totalMacroGrams) * 100) : 0,
              },
            },
            nutrients: {
              fiber: analysis.fiberGrams || 0,
              sugars: 0, // Not available from text analysis
            },
          };

          // Close this modal and open FoodFactsModal via parent
          handleClose();
          onTextAnalysisComplete?.(foodAnalysis);
        } else {
          throw new Error(data.error);
        }
      } else {
        throw new Error('Failed to analyze food');
      }
    } catch (error) {
      console.error('Error analyzing food:', error);
      if (isMountedRef.current) {
        setAnalyzeError('Failed to analyze food. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsAnalyzing(false);
      }
    }
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onCameraCapture) {
      onCameraCapture(file);
      handleClose();
    }
  };

  const getMealTitle = (type: string) => {
    const titles: Record<string, string> = {
      breakfast: 'Add Breakfast',
      lunch: 'Add Lunch',
      dinner: 'Add Dinner',
      snacks: 'Add Snack',
    };
    return titles[type] || 'Add Food';
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={handleClose}>
      <Modal
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-food-title"
        tabIndex={-1}
      >
        <Header>
          <Title id="add-food-title">{getMealTitle(mealType)}</Title>
          <CloseButton onClick={handleClose} aria-label="Close">
            <X size={20} weight="bold" />
          </CloseButton>
        </Header>

        <TabsContainer>
          <Tab $active={activeTab === 'camera'} onClick={() => setActiveTab('camera')}>
            <Camera size={18} />
            Camera
          </Tab>
          <Tab $active={activeTab === 'text'} onClick={() => setActiveTab('text')}>
            <TextT size={18} />
            Text
          </Tab>
        </TabsContainer>

        <Content>
          {activeTab === 'camera' ? (
            <CameraSection>
              <HiddenInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
              />
              <CameraButton onClick={handleCameraClick}>
                <Camera size={40} />
                <span>Take Photo</span>
              </CameraButton>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 13, textAlign: 'center', marginTop: 16 }}>
                Take a photo of your food and we'll analyze its nutritional content
              </p>
            </CameraSection>
          ) : (
            <TextSection>
              <InputGroup>
                <Label>Describe your food</Label>
                <TextArea
                  placeholder="e.g., 2 scrambled eggs with toast and butter, a glass of orange juice"
                  value={foodDescription}
                  onChange={(e) => setFoodDescription(e.target.value)}
                />
              </InputGroup>

              <AnalyzeButton
                $disabled={!foodDescription.trim() || isAnalyzing}
                onClick={handleAnalyze}
              >
                {isAnalyzing ? (
                  <>
                    <Spinner size={18} />
                    Analyzing...
                  </>
                ) : (
                  'Analyze Nutrition'
                )}
              </AnalyzeButton>

              {analyzeError && <ErrorText role="alert">{analyzeError}</ErrorText>}
            </TextSection>
          )}
        </Content>
      </Modal>
    </Overlay>
  );
};
